/**
 * GraphQL → TradingView `Bar` transforms.
 *
 * Absorbs the surviving logic from the deleted
 * `apps/bullrun-web/src/components/Chart/tradingChartHelpers.ts` and
 * `predictions/probabilityChartHelpers.ts`. Keeping the transforms in
 * `packages/react` lets the mobile-on-web `.web.tsx` wrappers share the
 * same code path as the web Datafeed.
 *
 * Scaling: backend prices arrive as BigInt scaled by `BIGINT_SCALE_FACTOR`
 * (1e6). All three transforms divide by that factor exactly once, before
 * emitting `Bar`s. Live `subscribeBars` ticks must apply the same scaling
 * (see datafeed.ts), or the live segment will sit ~6 orders of magnitude
 * above the historical segment.
 */
import { notEmpty } from "@orb-labs/types";
import { ohlcToCandleBars, pricePointsToLineBars, resolveTokenChartBars, } from "../../bullrun/tokenChartSeries.js";
import { pointsToProbabilityLineSeries } from "../../bullrun/predictionChartSeries.js";
function chartCandleBarToTVBar(bar) {
    return {
        time: bar.time * 1000,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume,
    };
}
/** Filter bars to the [from, to] window — both unix seconds. */
export function filterBarsToWindow(bars, from, to) {
    const fromMs = from * 1000;
    const toMs = to * 1000;
    return bars.filter((b) => b.time >= fromMs && b.time <= toMs);
}
/**
 * Token bars: prefer OHLC candles when trustworthy, otherwise the
 * price-only line. Matches the lightweight-charts behavior the old
 * `useTradingChartEngine` shipped via `resolveTokenChartBars`.
 */
export function tokenChartDataToBars(data) {
    const resolved = resolveTokenChartBars(data);
    return resolved.bars.map(chartCandleBarToTVBar);
}
/** Perp bars: OHLC only — perps always have priceOHLC populated. */
export function perpChartDataToBars(data) {
    if (!data?.priceOHLC?.length)
        return [];
    return ohlcToCandleBars(data.priceOHLC).map(chartCandleBarToTVBar);
}
/**
 * Prediction bars: probability is rendered as a line (TV `visible_plots_set:
 * "c"`), so all four OHLC fields collapse to `close`. Values arrive as
 * 0–100 percentages from `pointsToProbabilityLineSeries`; the chart's
 * priceFormatter renders them as `XX.X%`.
 */
export function predictionPricePointsToBars(points) {
    if (!notEmpty(points) || points.length === 0)
        return [];
    return pointsToProbabilityLineSeries(points).map((p) => {
        const { value } = p;
        return {
            time: p.time * 1000,
            open: value,
            high: value,
            low: value,
            close: value,
            volume: 0,
        };
    });
}
/** Prediction bars from a price-only `priceFallback` series (no OHLC available). */
export function tokenPriceSeriesToBars(points) {
    if (!notEmpty(points) || points.length === 0)
        return [];
    return pricePointsToLineBars(points).map(chartCandleBarToTVBar);
}
